import { useEffect } from 'react';

import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { Search, X } from '@/shared/components/icons';
import useDebounce from '@/shared/hooks/useDebounce';

interface TableSearchProps {
  initialSearchValue: string | undefined;
  debounceSearchChangeCallback: (value: string) => void;
  textFieldprops?: TextFieldProps;
}

const SearchField = ({
  initialSearchValue,
  debounceSearchChangeCallback,
  textFieldprops = {},
}: TableSearchProps) => {
  const {
    debouncedValue: debouncedSearchValue,
    value: searchValue,
    setValue: setSearchValue,
    forceSetValue: forceSetSearchValue,
  } = useDebounce(initialSearchValue ?? '');

  useEffect(() => {
    setSearchValue(initialSearchValue ?? '');
    forceSetSearchValue(initialSearchValue ?? '');
  }, [forceSetSearchValue, initialSearchValue, setSearchValue]);

  useEffect(() => {
    debounceSearchChangeCallback(debouncedSearchValue);
  }, [debounceSearchChangeCallback, debouncedSearchValue]);

  return (
    <TextField
      size='small'
      value={searchValue}
      aria-label='Search'
      onChange={(event) => {
        setSearchValue(event.target.value);
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position='start'>
              <Search
                fontSize='small'
                sx={{
                  color: (theme) =>
                    textFieldprops.disabled
                      ? theme.palette.text.disabled
                      : theme.palette.text.secondary,
                }}
              />
            </InputAdornment>
          ),
          endAdornment: searchValue ? (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                aria-label='clear search'
                edge='end'
                onClick={() => {
                  setSearchValue('');
                  forceSetSearchValue('');
                }}
              >
                <X fontSize='small' />
              </IconButton>
            </InputAdornment>
          ) : null,
        },
      }}
      sx={{ width: 400 }}
      {...textFieldprops}
    />
  );
};

export default SearchField;
